import { DATE_TIME_FORMAT } from "@frontend-shared/constants";
import { format, formatDistanceToNow } from "date-fns";
import { memo, useCallback, useMemo } from "react";

import { useMarkAsRead } from "@/api/notifications/useMarkAsRead";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface INotificationCardShellProps {
  uuid: string;
  // NOTE: Used as the data-testid suffix (e.g. "model_created" →
  // `notification-card-model_created`) so e2e specs can scope assertions to a
  // specific variant without depending on body text (i18n changes).
  variantTestId: string;
  icon: React.ReactNode;
  iconBgClassName?: string;
  body: React.ReactNode;
  createdAt: string;
  isRead: boolean;
  ctaLabel?: string;
  onCtaClick?: () => void;
  secondaryCtaLabel?: string;
  onSecondaryCtaClick?: () => void;
}

const NotificationCardShellComponent = ({
  uuid,
  variantTestId,
  icon,
  iconBgClassName,
  body,
  createdAt,
  isRead,
  ctaLabel,
  onCtaClick,
  secondaryCtaLabel,
  onSecondaryCtaClick,
}: INotificationCardShellProps): React.JSX.Element => {
  const markAsReadMutation = useMarkAsRead();

  // NOTE: Compute both representations once per render rather than calling
  // `new Date(createdAt)` twice. `formatDistanceToNow` returns the human
  // "1 day ago" string; `format` with DATE_TIME_FORMAT produces the
  // absolute timestamp the tooltip shows on hover.
  const { relativeTime, absoluteTime } = useMemo(() => {
    const createdAtDate = new Date(createdAt);
    return {
      relativeTime: formatDistanceToNow(createdAtDate, { addSuffix: true }),
      absoluteTime: format(createdAtDate, DATE_TIME_FORMAT),
    };
  }, [createdAt]);

  const handleClick = useCallback((): void => {
    if (!isRead) {
      markAsReadMutation.mutate({ uuid });
    }
  }, [isRead, markAsReadMutation, uuid]);

  const handleCtaClick = useCallback(
    (event: React.MouseEvent): void => {
      event.stopPropagation();
      if (!isRead) {
        markAsReadMutation.mutate({ uuid });
      }
      onCtaClick?.();
    },
    [isRead, markAsReadMutation, uuid, onCtaClick],
  );

  const handleSecondaryCtaClick = useCallback(
    (event: React.MouseEvent): void => {
      event.stopPropagation();
      if (!isRead) {
        markAsReadMutation.mutate({ uuid });
      }
      onSecondaryCtaClick?.();
    },
    [isRead, markAsReadMutation, uuid, onSecondaryCtaClick],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={`notification-card-${variantTestId}`}
      data-uuid={uuid}
      data-read={isRead ? "true" : "false"}
      className={cn(
        // NOTE: `overflow-hidden` is a safety net so a child that escapes the
        // break-wrap rules below can never widen the card / drawer. Without
        // it, a long unbreakable token (e.g. a malformed assistant name) used
        // to push the drawer to scroll horizontally.
        "group relative w-full overflow-hidden text-left p-4 rounded-xl border border-border/60 bg-card transition-all",
        "shadow-sm hover:shadow-md hover:border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        !isRead && "bg-primary/[0.04] border-primary/20",
      )}
    >
      {!isRead && (
        <span
          aria-hidden="true"
          className="absolute top-4 right-4 h-2 w-2 rounded-full bg-primary"
        />
      )}
      <div className="flex items-start gap-3 min-w-0">
        <div
          className={cn(
            "flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-primary",
            iconBgClassName ?? "bg-primary/10",
          )}
        >
          {icon}
        </div>
        {/* NOTE: `min-w-0` on this flex item AND on its parent row is what
            actually allows the child below to honour the wrap rules. Without
            it, the default `min-width: auto` lets the flex item grow to fit
            its longest unbreakable child, defeating overflow-wrap entirely.
            `pr-6` (was pr-4) gives the absolutely-positioned unread dot at
            `right-4` a 24px lane so long text never sits underneath it. */}
        <div className="flex-1 min-w-0 pr-6">
          {/* NOTE: `[overflow-wrap:anywhere]` is the generic wrap rule for
              EVERY notification body across EVERY variant. Unlike
              `overflow-wrap: break-word`, `anywhere` lets the browser break
              even when there's no soft break opportunity — so a long
              unbreakable string (assistant name, file name, project title,
              prompt name, etc.) always wraps inside the card width.
              `min-w-0` belt-and-braces guarantees the flex chain can
              actually shrink to honour the break. */}
          <div className="min-w-0 text-sm leading-5 text-foreground [overflow-wrap:anywhere]">
            {body}
          </div>
          {/* NOTE: Relative time ("1 day ago") is the primary glance — but
              users often want the exact timestamp. The tooltip renders the
              absolute date+time in the app-wide common format
              (DATE_TIME_FORMAT). `asChild` keeps the trigger a span so we
              don't nest a <button> inside the card's own button element.
              Local TooltipProvider because the app has no global one.
              `side="right"` opens the tooltip into the empty space beside the
              timestamp instead of over the CTA row that sits
              directly beneath it. `disableHoverableContent` stops Radix from
              keeping the tooltip open when the pointer drifts onto the content
              on its way to the "View meeting" link — without it the tooltip
              stays stuck after the cursor leaves the timestamp. */}
          <TooltipProvider delayDuration={200} disableHoverableContent>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  data-testid={`notification-card-${variantTestId}-timestamp`}
                  className="mt-1.5 inline-block text-xs text-muted-foreground"
                >
                  {relativeTime}
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">{absoluteTime}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {(Boolean(ctaLabel && onCtaClick) ||
            Boolean(secondaryCtaLabel && onSecondaryCtaClick)) && (
            <div className="mt-2 flex items-center gap-3">
              {Boolean(ctaLabel && onCtaClick) && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs font-medium"
                  onClick={handleCtaClick}
                  data-testid={`notification-card-${variantTestId}-cta`}
                >
                  {ctaLabel}
                </Button>
              )}
              {Boolean(secondaryCtaLabel && onSecondaryCtaClick) && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs font-medium"
                  onClick={handleSecondaryCtaClick}
                  data-testid={`notification-card-${variantTestId}-secondary-cta`}
                >
                  {secondaryCtaLabel}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

export const NotificationCardShell = memo(NotificationCardShellComponent);
